import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, ArrowRight, CheckCircle, ArrowLeft, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function CandidateAccessPage() {
  const { t, language, direction } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال بريدك الإلكتروني' : 'Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/candidate/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() })
      });
      
      if (response.ok) {
        setSuccess(true);
      } else {
        toast.error(language === 'ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى.' : 'An error occurred. Please try again.');
      }
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الاتصال' : 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center p-4" dir={direction}>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#111827] mb-3">
              {language === 'ar' ? 'تحقق من بريدك الإلكتروني' : 'Check Your Email'}
            </h2>
            <p className="text-[#6B7280] mb-6">
              {language === 'ar' 
                ? `إذا كان لديك طلبات مرتبطة بـ ${email}، سوف تتلقى رابط الوصول قريباً.`
                : `If you have applications linked to ${email}, you'll receive an access link shortly.`
              }
            </p>
            <p className="text-sm text-[#9CA3AF] mb-6">
              {language === 'ar' 
                ? 'الرابط صالح لمدة 24 ساعة.'
                : 'The link is valid for 24 hours.'
              }
            </p>
            <div className="flex flex-col gap-3">
              <Button variant="outline" onClick={() => { setSuccess(false); setEmail(''); }}>
                {language === 'ar' ? 'إرسال رابط جديد' : 'Send Another Link'}
              </Button>
              <Link to="/">
                <Button variant="ghost" className="w-full">
                  <ArrowLeft className="h-4 w-4 me-2" />
                  {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center p-4" dir={direction}>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-[#33AADD]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Briefcase className="h-7 w-7 text-[#33AADD]" />
          </div>
          <CardTitle className="text-2xl">
            {language === 'ar' ? 'بوابة المتقدمين' : 'Candidate Portal'}
          </CardTitle>
          <CardDescription>
            {language === 'ar' 
              ? 'أدخل بريدك الإلكتروني للوصول إلى طلباتك'
              : 'Enter your email to access your applications'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">
                {language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
              </Label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  className="ps-10"
                  data-testid="candidate-email-input"
                  required
                />
              </div>
              <p className="text-xs text-[#6B7280]">
                {language === 'ar' 
                  ? 'استخدم نفس البريد الإلكتروني الذي استخدمته للتقديم على الوظائف'
                  : 'Use the same email you used when applying for jobs'
                }
              </p>
            </div>
            
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="request-access-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {language === 'ar' ? 'إرسال رابط الوصول' : 'Send Access Link'}
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-[#6B7280] mb-3">
              {language === 'ar' ? 'لم تقدم بعد؟' : "Haven't applied yet?"}
            </p>
            <Link to="/jobs">
              <Button variant="outline" size="sm">
                <Briefcase className="h-4 w-4 me-2" />
                {language === 'ar' ? 'تصفح الوظائف' : 'Browse Jobs'}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
